import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Star, Eye, Plus, Minus, Zap } from 'lucide-react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Product } from '@/types';
import { useMemo, useState } from 'react';

interface ProductCardProps {
  product: Product;
  className?: string;
  serverId?: string;
}

export function ProductCard({ product, className = '', serverId }: ProductCardProps) {
  const { t } = useTranslation();
  const { addItem, updateQuantity, items } = useCart();
  const { config } = useTheme();
  const { isAuthenticated, openLoginModal } = useAuth();
  const navigate = useNavigate();
  const minQty = product.minQuantity || 1;
  const [showQuantitySelector, setShowQuantitySelector] = useState(false);

  // Verificar se o produto já está no carrinho
  const cartItem = useMemo(() => 
    items.find(item => item.productId === product.id),
    [items, product.id]
  );

  // Se está no carrinho, sempre mostra o seletor
  const shouldShowSelector = showQuantitySelector || !!cartItem;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(price);
  };

  // Chave de licença sem estoque: não entra no carrinho (a API também recusa no pedido).
  const esgotado = !!product.soldOut;

  const handleShowQuantitySelector = (e: React.MouseEvent) => {
    e.preventDefault();
    if (esgotado) {
      toast.error(t('store.digital.sold_out_toast'));
      return;
    }
    
    // Verificar se está autenticado
    if (!isAuthenticated) {
      toast.error('Login necessário', {
        description: 'Você precisa estar logado para adicionar produtos ao carrinho.'
      });
      openLoginModal();
      return;
    }
    
    setShowQuantitySelector(true);
    
    // Adiciona com quantidade mínima
    addItem(product.id, minQty);
    
    if (minQty > 1) {
      toast.success(`${product.name} adicionado!`, {
        description: `Quantidade mínima: ${minQty} unidades.`
      });
    } else {
      toast.success(`${product.name} adicionado ao carrinho!`);
    }
  };

  const handleBuyNow = (e: React.MouseEvent) => {
    e.preventDefault();
    if (esgotado) {
      toast.error(t('store.digital.sold_out_toast'));
      return;
    }
    
    // Verificar se está autenticado
    if (!isAuthenticated) {
      toast.error('Login necessário', {
        description: 'Você precisa estar logado para comprar produtos.'
      });
      openLoginModal();
      return;
    }
    
    // Se já está no carrinho, apenas redireciona
    if (!cartItem) {
      // Adiciona ao carrinho com quantidade mínima
      addItem(product.id, minQty);
      
      toast.success(`${product.name} adicionado!`, {
        description: 'Redirecionando para o carrinho...'
      });
    }
    
    // Redireciona para o carrinho/checkout
    setTimeout(() => {
      navigate('/carrinho');
    }, cartItem ? 0 : 500);
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Login necessário');
      openLoginModal();
      return;
    }
    
    const currentQty = cartItem?.quantity || minQty;
    updateQuantity(product.id, currentQty + 1);
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Login necessário');
      openLoginModal();
      return;
    }
    
    const currentQty = cartItem?.quantity || minQty;
    const newQty = currentQty - 1;
    
    if (newQty < minQty) {
      // Remove do carrinho
      updateQuantity(product.id, 0);
      setShowQuantitySelector(false);
      toast.info(`${product.name} removido do carrinho`);
    } else {
      updateQuantity(product.id, newQty);
    }
  };

  const discountPercent = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  return (
    <Card className={`group overflow-hidden card-interactive bg-card border-border flex flex-col h-full ${className}`}>
      {/* Image */}
      <div className="p-4 pb-0">
        {/* No celular (1 coluna) a imagem quadrada deixava o card com ~500 px de altura. */}
        <div className="relative w-full aspect-[4/3] sm:aspect-square overflow-hidden rounded-md">
          <img
            src={product.image}
            alt={product.name}
            className="absolute inset-0 w-full h-full object-contain sm:object-cover transition-transform duration-500 group-hover:scale-110"
          />
        <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent" />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {product.featured && (
            <Badge className="bg-accent text-accent-foreground">
              <Star className="w-3 h-3 mr-1" />
              Destaque
            </Badge>
          )}
          {discountPercent > 0 && (
            <Badge variant="destructive">
              -{discountPercent}%
            </Badge>
          )}
        </div>
        </div>
      </div>

      {/* Content */}
      <CardContent className="p-4 flex-1">
        <Link to={serverId && product.categorySlug ? `/loja/${serverId}/${product.categorySlug}/produto/${product.slug}` : product.categorySlug ? `/loja/${product.categorySlug}/produto/${product.slug}` : `/loja/produto/${product.slug}`}>
          <h3 className="font-display font-semibold text-lg text-foreground hover:text-primary transition-colors line-clamp-1">
            {product.name}
          </h3>
        </Link>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
          {product.shortDescription || product.description}
        </p>
        
        {/* Duration */}
        <Badge variant="outline" className="mt-2 text-xs">
          {product.durationDays
            ? `${product.durationDays} ${product.durationDays === 1 ? 'dia' : 'dias'}`
            : 'Permanente'}
        </Badge>
      </CardContent>

      {/* Footer */}
      <CardFooter className="p-4 pt-0 flex items-center justify-between mt-auto gap-2">
        <div className="flex-1">
          {product.originalPrice ? (
            // Tem promoção: mostrar preço original riscado e promocional em destaque
            <>
              <p className="text-sm text-muted-foreground line-through mb-0.5">
                {formatPrice(product.originalPrice)}
              </p>
              <p className="font-display font-bold text-2xl text-primary">
                {formatPrice(product.price)}
              </p>
            </>
          ) : (
            // Sem promoção: mostrar apenas o preço normal
            <p className="font-display font-bold text-xl text-primary">
              {formatPrice(product.price)}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Link to={serverId && product.categorySlug ? `/loja/${serverId}/${product.categorySlug}/produto/${product.slug}` : product.categorySlug ? `/loja/${product.categorySlug}/produto/${product.slug}` : `/loja/produto/${product.slug}`}>
            <Button
              size="sm"
              variant="outline"
              className="border-border hover:bg-background/50"
            >
              <Eye className="w-4 h-4" />
            </Button>
          </Link>
          
          {/* Se deve mostrar seletor (clicou em adicionar ou já está no carrinho) */}
          {shouldShowSelector ? (
            <div className="flex items-center gap-1 flex-1">
              <Button
                size="sm"
                variant="outline"
                className="h-9 w-9 p-0"
                onClick={handleDecrement}
              >
                <Minus className="w-4 h-4" />
              </Button>
              <div className="flex-1 text-center font-semibold text-sm px-2 bg-background/50 rounded border border-border h-9 flex items-center justify-center">
                {cartItem?.quantity || minQty}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-9 w-9 p-0"
                onClick={handleIncrement}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            /* Dois botões: Adicionar ao Carrinho e Comprar Agora */
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={handleShowQuantitySelector}
                className="flex-1 border-primary text-primary hover:bg-primary/10"
                title="Adicionar ao carrinho"
              >
                <ShoppingCart className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                onClick={handleBuyNow}
                disabled={esgotado}
                className="flex-1 bg-primary hover:bg-primary-hover text-primary-foreground"
              >
                <Zap className="w-4 h-4 mr-1" />
                {esgotado ? t('store.digital.sold_out') : 'Comprar'}
              </Button>
            </>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}

export default ProductCard;
